import {DataRouterApplication} from '@github-ui/react-core/future/data-router-application'
import {registerDataRouterApp} from '@github-ui/react-core/register-app'
import {Outlet} from '@github-ui/react-router'
import {useEffect} from 'react'

import {startCopilotRootReadiness, type CopilotUiReadinessView} from '../copilot-ui-readiness'
import {CopilotUiReadinessContext, useCopilotUiReadinessLifetime} from '../copilot-ui-readiness-context'

const previousViews = new WeakMap<HTMLElement, CopilotUiReadinessView>()

function CopilotReadinessRoot({view}: {view: CopilotUiReadinessView | undefined}) {
  useCopilotUiReadinessLifetime(view, 'replaced')
  useEffect(() => {
    view?.initialized()
  }, [view])

  return (
    <CopilotUiReadinessContext value={view}>
      <Outlet />
    </CopilotUiReadinessContext>
  )
}

class CopilotReadinessApp<T extends string> extends DataRouterApplication<T> {
  readonly #app: DataRouterApplication<T>

  constructor(app: DataRouterApplication<T>) {
    super(app.name, [], {tanStackRouterEnabled: app.tanStackRouterEnabled})
    this.#app = app
  }

  override registration(options?: Parameters<DataRouterApplication<T>['registration']>[0]) {
    const element =
      typeof document === 'undefined'
        ? null
        : [...document.querySelectorAll<HTMLElement>('react-app[app-name]')].find(
            root => root.getAttribute('app-name') === this.#app.name,
          )
    const previous = element ? previousViews.get(element) : undefined
    const view =
      typeof window === 'undefined'
        ? undefined
        : startCopilotRootReadiness(this.#app.name, previous?.ready ? undefined : previous?.open)
    if (view && previous && previous !== view) previous.dispose('replaced')
    if (element && view) previousViews.set(element, view)

    try {
      const {routes} = this.#app.registration(options)
      if (view) {
        view.observeOrigin(options?.embeddedData)
        view.configurationAvailable(undefined, true)
      }
      return {
        routes: [
          {
            Component: () => <CopilotReadinessRoot view={view} />,
            children: routes,
          },
        ],
      }
    } catch (error) {
      view?.failure('registration')
      throw error
    }
  }
}

export function registerCopilotReadinessApp<T extends string>(app: DataRouterApplication<T>) {
  registerDataRouterApp(new CopilotReadinessApp(app))
}
